import { motion } from "framer-motion";
import type { CSSProperties } from "react";

interface SerifGlowProps {
  word: string;
  fontSize: number;
  lineHeight?: number;
  letterSpacing?: number;
  strokeWidth: number;
  delay?: number;
  italic?: boolean;
  fillColor?: string;
  style?: CSSProperties;
}

const TEXT_COLOR = "#545454";
const GLOW_COLOR = "#FFB454";

/**
 * Signature Instrument Serif accent word with dual-layer yellow-green glow
 * and the "tubular curl" entrance animation.
 */
export default function SerifGlow({
  word,
  fontSize,
  lineHeight,
  letterSpacing,
  strokeWidth,
  delay = 0,
  italic = false,
  fillColor = TEXT_COLOR,
  style,
}: SerifGlowProps) {
  const sharedTypeStyle: CSSProperties = {
    fontFamily: "'Instrument Serif', serif",
    fontStyle: italic ? "italic" : "normal",
    fontWeight: 400,
    fontSize: `${fontSize}px`,
    lineHeight: lineHeight ? `${lineHeight}px` : "1",
    letterSpacing: letterSpacing != null ? `${letterSpacing}px` : "normal",
  };

  return (
    <motion.span
      style={{
        display: "inline-block",
        position: "relative",
        transformPerspective: 600,
        transformOrigin: "top center",
        ...style,
      }}
      initial={{ rotateX: -110, scaleY: 0.15, scaleX: 0.7, opacity: 0 }}
      animate={{
        rotateX: [-110, -70, -20, 5, -2, 0],
        scaleY: [0.15, 0.4, 0.8, 1.04, 0.98, 1],
        scaleX: [0.7, 0.85, 0.95, 1.02, 1, 1],
        opacity: [0, 0.4, 0.85, 1, 1, 1],
      }}
      transition={{
        duration: 0.7,
        delay,
        ease: [0.22, 1, 0.36, 1],
        times: [0, 0.2, 0.55, 0.75, 0.88, 1],
      }}
    >
      <span
        aria-hidden="true"
        style={{
          ...sharedTypeStyle,
          position: "absolute",
          top: 0,
          left: 0,
          color: GLOW_COLOR,
          WebkitTextStrokeWidth: `${strokeWidth}px`,
          WebkitTextStrokeColor: GLOW_COLOR,
          pointerEvents: "none",
          whiteSpace: "nowrap",
        }}
      >
        {word}
      </span>
      <span
        style={{
          ...sharedTypeStyle,
          position: "relative",
          color: fillColor,
          whiteSpace: "nowrap",
        }}
      >
        {word}
      </span>
    </motion.span>
  );
}

/**
 * Variant that only plays the entrance when scrolled into view.
 */
export function SerifGlowInView({
  word,
  fontSize,
  lineHeight,
  letterSpacing,
  strokeWidth,
  delay = 0,
  italic = false,
  fillColor = TEXT_COLOR,
  style,
}: SerifGlowProps) {
  const sharedTypeStyle: CSSProperties = {
    fontFamily: "'Instrument Serif', serif",
    fontStyle: italic ? "italic" : "normal",
    fontWeight: 400,
    fontSize: `${fontSize}px`,
    lineHeight: lineHeight ? `${lineHeight}px` : "1",
    letterSpacing: letterSpacing != null ? `${letterSpacing}px` : "normal",
  };

  return (
    <motion.span
      style={{
        display: "inline-block",
        position: "relative",
        transformPerspective: 600,
        transformOrigin: "top center",
        ...style,
      }}
      initial={{ rotateX: -110, scaleY: 0.15, scaleX: 0.7, opacity: 0 }}
      whileInView={{
        rotateX: [-110, -70, -20, 5, -2, 0],
        scaleY: [0.15, 0.4, 0.8, 1.04, 0.98, 1],
        scaleX: [0.7, 0.85, 0.95, 1.02, 1, 1],
        opacity: [0, 0.4, 0.85, 1, 1, 1],
      }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{
        duration: 0.7,
        delay,
        ease: [0.22, 1, 0.36, 1],
        times: [0, 0.2, 0.55, 0.75, 0.88, 1],
      }}
    >
      <span
        aria-hidden="true"
        style={{
          ...sharedTypeStyle,
          position: "absolute",
          top: 0,
          left: 0,
          color: GLOW_COLOR,
          WebkitTextStrokeWidth: `${strokeWidth}px`,
          WebkitTextStrokeColor: GLOW_COLOR,
          pointerEvents: "none",
          whiteSpace: "nowrap",
        }}
      >
        {word}
      </span>
      <span
        style={{
          ...sharedTypeStyle,
          position: "relative",
          color: fillColor,
          whiteSpace: "nowrap",
        }}
      >
        {word}
      </span>
    </motion.span>
  );
}